import React from "react";
import {format} from "date-fns";
import {Check, ChevronDown, Sparkles} from "lucide-react";
import {useTranslation} from "react-i18next";
import {useDateFnsLocale} from "../../../i18n/dateLocale";
import Button from "../../Common/Button";
import MenuSheet from "./MenuSheet";

// Quick periods, grouped (day / week / month / longer). Same ranges as before.
export const QUICK_PERIOD_GROUPS = [
    ["today", "yesterday"],
    ["thisWeek", "lastWeek"],
    ["thisMonth", "lastMonth"],
    ["thisQuarter", "last6Months", "thisYear", "lastYear"],
];

export const getQuickPeriodRange = (value, today = new Date()) => {
    const y = today.getFullYear();
    const m = today.getMonth();
    // Monday of the current week
    const monday = new Date(y, m, today.getDate() - ((today.getDay() + 6) % 7));
    switch (value) {
        case "today":
            return {startDate: new Date(today), endDate: new Date(today)};
        case "yesterday": {
            const d = new Date(y, m, today.getDate() - 1);
            return {startDate: d, endDate: new Date(d)};
        }
        case "thisWeek":
            return {startDate: monday, endDate: new Date(today)};
        case "lastWeek": {
            const start = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() - 7);
            return {startDate: start, endDate: new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6)};
        }
        case "thisMonth":
            return {startDate: new Date(y, m, 1), endDate: new Date(y, m + 1, 0)};
        case "lastMonth":
            return {startDate: new Date(y, m - 1, 1), endDate: new Date(y, m, 0)};
        case "thisQuarter": {
            const q = Math.floor(m / 3) * 3;
            return {startDate: new Date(y, q, 1), endDate: new Date(y, q + 3, 0)};
        }
        case "last6Months": {
            const start = new Date(today);
            start.setMonth(m - 6);
            return {startDate: start, endDate: new Date(today)};
        }
        case "thisYear":
            return {startDate: new Date(y, 0, 1), endDate: new Date(y, 11, 31)};
        case "lastYear":
            return {startDate: new Date(y - 1, 0, 1), endDate: new Date(y - 1, 11, 31)};
        default:
            return null;
    }
};

/**
 * Header dropdown of quick periods, styled like ComparePeriodSelector:
 * the selected period is teal with a check, and each option shows its dates.
 */
const QuickPeriodSelector = ({isOpen, setIsOpen, selectedPeriod, setSelectedPeriod, setDateRange, isDarkMode = false}) => {
    const {t} = useTranslation("activite");
    const locale = useDateFnsLocale();
    // `selectedPeriod` is a stable key ("today", ... or the "quick" sentinel),
    // never a pre-translated string, so it re-resolves on language change.
    const label = selectedPeriod === "quick"
        ? t("activityPage.quickSelection")
        : t(`pageHeader.periods.${selectedPeriod}`, {defaultValue: selectedPeriod});

    const formatRange = ({startDate, endDate}) => {
        const sameDay = startDate.toDateString() === endDate.toDateString();
        const sameYear = startDate.getFullYear() === endDate.getFullYear();
        if (sameDay) return format(startDate, "d MMM yyyy", {locale});
        return `${format(startDate, sameYear ? "d MMM" : "d MMM yyyy", {locale})} → ${format(endDate, "d MMM yyyy", {locale})}`;
    };

    const choose = (value) => {
        const range = getQuickPeriodRange(value);
        setDateRange([{...range, key: "selection"}]);
        setSelectedPeriod(value);
        setIsOpen(false);
    };

    return (
        <MenuSheet
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            title={t("pageHeader.quickPeriods")}
            isDarkMode={isDarkMode}
            testId="quick-period-selector"
            trigger={
                <Button
                    variant="plain"
                    icon={Sparkles}
                    iconRight={ChevronDown}
                    compact
                    aria-haspopup="menu"
                    aria-expanded={isOpen}
                    aria-label={t("pageHeader.periodAriaLabel", {period: label})}
                    className={isOpen ? "bg-primary/10" : ""}
                    onClick={() => setIsOpen(!isOpen)}
                >
                    {label}
                </Button>
            }
        >
            {QUICK_PERIOD_GROUPS.map((group, i) => (
                <div key={group[0]} className={`py-1 ${i > 0 ? `border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"}` : ""}`}>
                    {group.map((value) => {
                        const selected = selectedPeriod === value;
                        return (
                            <button
                                key={value}
                                type="button"
                                role="menuitemradio"
                                aria-checked={selected}
                                onClick={() => choose(value)}
                                className={`flex items-center gap-3 w-full text-left px-4 py-2.5 sm:py-2 text-sm transition-colors ${
                                    selected
                                        ? "bg-primary/5 text-primary"
                                        : isDarkMode
                                            ? "text-gray-200 hover:bg-gray-700/80"
                                            : "text-gray-700 hover:bg-gray-50"
                                }`}
                            >
                                <span className="flex-1 min-w-0">
                                    <span className="block font-medium">{t(`pageHeader.periods.${value}`)}</span>
                                    <span className={`block text-xs mt-0.5 ${
                                        selected ? "text-primary/80" : isDarkMode ? "text-gray-400" : "text-gray-500"
                                    }`}>
                                        {formatRange(getQuickPeriodRange(value))}
                                    </span>
                                </span>
                                {selected && <Check className="w-4 h-4 flex-shrink-0"/>}
                            </button>
                        );
                    })}
                </div>
            ))}
        </MenuSheet>
    );
};

export default QuickPeriodSelector;
